"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardSummaryQuery = exports.UpdateDashboardWidgetsRequest = exports.DashboardWidgetSelection = exports.DASHBOARD_WIDGET_MODULE = exports.DASHBOARD_DEFAULT_WIDGETS = exports.DashboardSummary = exports.DashboardWidgetId = exports.DASHBOARD_WIDGETS = exports.DashboardFiscalHolds = exports.DashboardWeight = exports.DashboardKitchen = exports.DashboardKitchenLocation = exports.DashboardPayments = exports.DashboardApprovals = exports.DashboardCash = exports.DashboardSync = exports.DashboardShifts = exports.DashboardTerminals = exports.DashboardSales = exports.DashboardWindow = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * BOS dashboard (RL-377).
 *
 * The dashboard answers two questions in one screen — "is anything broken" and
 * "is money coming in" — and every tile deep-links into a page that already
 * exists. `docs/design/bos-dashboard-concept.md` carries the decisions behind
 * this shape; the ones that show up here are worth repeating:
 *
 * - Sales are gross, returns and net as THREE fields, never one collapsed
 *   number. A quiet day and a heavy-returns day must not look identical.
 * - Money crosses the wire as a decimal minor-unit string, never a JSON number.
 *   `Receipt.totalMinor` is a BigInt; RL-363 is what happens when that rule is
 *   broken in the other direction.
 * - Every block is optional. The server omits what the caller has no permission
 *   to see, so the client renders what it may and never probes for 403s.
 */
/**
 * The day window, resolved server-side and echoed back.
 *
 * There is no tenant-level timezone in the schema — only `Location.timezone` —
 * so the server resolves the tenant's zone from its locations and says which one
 * it used. Every live tenant with locations currently has exactly one distinct
 * zone, but an estate that straddles two would otherwise silently get somebody's
 * midnight, so the resolved zone is part of the answer rather than an assumption
 * the client has to make. `ambiguous` is true when the estate declares more than
 * one zone and the server fell back to UTC.
 */
exports.DashboardWindow = zod_1.z.object({
    /** The local date the figures cover, `YYYY-MM-DD` in `timeZone`. */
    date: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** IANA zone the day boundary was computed in; `UTC` when unresolvable. */
    timeZone: zod_1.z.string().min(1),
    ambiguous: zod_1.z.boolean(),
    /** Start of the window as an instant, for a client that wants to re-filter. */
    from: zod_1.z.string().datetime(),
    to: zod_1.z.string().datetime(),
});
/**
 * Trading for the window.
 *
 * `grossMinor` sums `sale` receipts and `returnedMinor` sums `refund` receipts as
 * a POSITIVE magnitude, so `netMinor = grossMinor - returnedMinor`. An original
 * sale is not removed when its lifecycle later becomes `partially_returned` or
 * `fully_returned` — the refund receipt carries the return side, and removing
 * both would understate the day twice.
 *
 * Receipts imported from a legacy system are excluded, so a backfill run at
 * lunchtime cannot look like a lunchtime rush.
 *
 * MICRO-43: container deposits are not sales. Every `lineRole: deposit` line is
 * left out of `grossMinor` and `returnedMinor`, and reported on its own:
 * `depositsCollectedMinor` on sales, `depositsRefundedMinor` (a POSITIVE
 * magnitude) on refunds.
 */
exports.DashboardSales = zod_1.z.object({
    /** Null when monetary rows have mixed or unknown currency. */
    grossMinor: pricing_js_1.MoneyMinor.nullable(),
    returnedMinor: pricing_js_1.MoneyMinor.nullable(),
    netMinor: pricing_js_1.MoneyMinor.nullable(),
    receiptCount: zod_1.z.number().int().min(0),
    returnCount: zod_1.z.number().int().min(0),
    /** MICRO-43: deposit lines on sales; null whenever `grossMinor` is. */
    depositsCollectedMinor: pricing_js_1.MoneyMinor.nullable(),
    /** MICRO-43: deposit lines on refunds, positive; null whenever `returnedMinor` is. */
    depositsRefundedMinor: pricing_js_1.MoneyMinor.nullable(),
    /** Null when the window produced no receipts, or they disagree on currency. */
    currency: zod_1.z.string().length(3).nullable(),
    /** Why money is unavailable; null for one known currency or an empty day. */
    moneyUnavailable: zod_1.z.enum(['mixed_currency', 'unknown_currency']).nullable(),
});
/** Tills reachable now. `online` uses the same 5-minute heartbeat the terminals list shows. */
exports.DashboardTerminals = zod_1.z.object({
    total: zod_1.z.number().int().min(0),
    online: zod_1.z.number().int().min(0),
    offline: zod_1.z.number().int().min(0),
});
/**
 * `open` counts the states a till is still trading in, as a NOW fact — a shift
 * opened yesterday and still running is exactly what an operator needs to see,
 * so it is not windowed.
 *
 * `abnormal` IS windowed, and the asymmetry is deliberate. `abnormal_close` is a
 * terminal state nothing ever clears, so an unwindowed count only grows: one bad
 * close in March would read "1" forever until the number stopped meaning
 * anything. Windowed on `closedAt`, it answers "did a till close badly today",
 * which is the question the tile is on the page to ask.
 */
exports.DashboardShifts = zod_1.z.object({
    open: zod_1.z.number().int().min(0),
    abnormal: zod_1.z.number().int().min(0),
});
/**
 * Sync lag folded to one count per colour, using each terminal's worst domain.
 * `unknown` is a terminal that has never reported, which is not the same as
 * healthy and must not be counted as green.
 */
exports.DashboardSync = zod_1.z.object({
    green: zod_1.z.number().int().min(0),
    yellow: zod_1.z.number().int().min(0),
    red: zod_1.z.number().int().min(0),
    unknown: zod_1.z.number().int().min(0),
});
/** Cash drawer variance for the window, summed across shifts. */
exports.DashboardCash = zod_1.z.object({
    varianceMinor: pricing_js_1.MoneyMinor,
    shiftCount: zod_1.z.number().int().min(0),
});
/**
 * Two separate numbers, deliberately not one "needs attention" total: approvals
 * and stuck payments have different owners and different drill-downs, and
 * summing them would hide which one is on fire.
 */
exports.DashboardApprovals = zod_1.z.object({ pending: zod_1.z.number().int().min(0) });
exports.DashboardPayments = zod_1.z.object({ stuck: zod_1.z.number().int().min(0) });
/** Kitchen orders still in play, and where. Present only for a tenant entitled to `kitchen`. */
exports.DashboardKitchenLocation = zod_1.z.object({
    locationId: zod_1.z.string().nullable(),
    locationName: zod_1.z.string().nullable(),
    activeOrders: zod_1.z.number().int().min(0),
});
exports.DashboardKitchen = zod_1.z.object({
    activeOrders: zod_1.z.number().int().min(0),
    byLocation: zod_1.z.array(exports.DashboardKitchenLocation),
});
/** Percent of weighed items covered, mirroring `/weight/coverage`'s own rollup. */
exports.DashboardWeight = zod_1.z.object({
    coveragePercent: zod_1.z.number().min(0).max(100),
});
/**
 * EAT-25: sales held for fiscal recovery on self-checkout lanes, now. `held`
 * counts every open hold (each blocks its lane); `autoRetriesExhausted` those
 * the lane gave up retrying on its own, which need a person; `stale` those on a
 * lane that has not reported in 15 minutes, so the figure may be out of date.
 */
exports.DashboardFiscalHolds = zod_1.z.object({
    held: zod_1.z.number().int().min(0),
    autoRetriesExhausted: zod_1.z.number().int().min(0),
    stale: zod_1.z.number().int().min(0),
});
/**
 * Every widget the dashboard can show.
 *
 * The id is the contract between the stored tenant selection, summary response,
 * and client, so these strings are persisted: renaming one is a migration, not
 * a refactor.
 */
exports.DASHBOARD_WIDGETS = [
    'sales',
    'posOnline',
    'syncHealth',
    'openShifts',
    'cashVariance',
    'approvals',
    'payments',
    'kitchenOrders',
    'weightCoverage',
    // EAT-25: held fiscal sales on self-checkout lanes; opt-in like kitchen.
    'fiscalHolds',
];
exports.DashboardWidgetId = zod_1.z.enum(exports.DASHBOARD_WIDGETS);
/**
 * One round trip for the whole page.
 *
 * Blocks are optional rather than nullable: an absent block means "not yours to
 * see, or not licensed", and the client simply does not render that tile. A
 * present block with zeros means the opposite — asked, answered, nothing there.
 */
exports.DashboardSummary = zod_1.z.object({
    window: exports.DashboardWindow,
    generatedAt: zod_1.z.string().datetime(),
    /** Tenant selection in render order, already intersected with entitlement. */
    widgets: zod_1.z.array(exports.DashboardWidgetId),
    sales: exports.DashboardSales.optional(),
    terminals: exports.DashboardTerminals.optional(),
    shifts: exports.DashboardShifts.optional(),
    sync: exports.DashboardSync.optional(),
    cash: exports.DashboardCash.optional(),
    approvals: exports.DashboardApprovals.optional(),
    payments: exports.DashboardPayments.optional(),
    kitchen: exports.DashboardKitchen.optional(),
    weight: exports.DashboardWeight.optional(),
    fiscalHolds: exports.DashboardFiscalHolds.optional(),
});
/**
 * What a tenant sees when nobody has chosen: the seven default tiles, in the
 * order the concept argues for — is it alive, what did it earn, what needs me.
 * Kitchen orders and weight coverage are opt-in per tenant.
 */
exports.DASHBOARD_DEFAULT_WIDGETS = [
    'sales',
    'posOnline',
    'syncHealth',
    'openShifts',
    'cashVariance',
    'approvals',
    'payments',
];
/**
 * A widget's availability is its module entitlement, which is a licence, not a
 * permission. A tenant without `kitchen` never sees the kitchen widget in the
 * picker at all.
 *
 * Every widget currently names a module, including the seven in
 * `DASHBOARD_DEFAULT_WIDGETS`. That matters: a tenant not entitled to `shifts`
 * has a built-in default containing two widgets it may not have. **Both the
 * stored selection and the default must be intersected with availability before
 * they are returned or rendered** — otherwise a tenant receives a `widgets` list
 * its own `available` contradicts. A widget added later with no entry here is
 * available to everyone.
 */
exports.DASHBOARD_WIDGET_MODULE = {
    kitchenOrders: 'kitchen',
    weightCoverage: 'weight',
    approvals: 'approvals',
    payments: 'payments',
    cashVariance: 'shifts',
    openShifts: 'shifts',
    sales: 'receipts',
    posOnline: 'terminal-management',
    syncHealth: 'terminal-management',
    fiscalHolds: 'terminal-management',
};
/** The tenant's chosen tiles. A singleton per tenant database, like OIDC and loyalty delegation. */
exports.DashboardWidgetSelection = zod_1.z.object({
    widgets: zod_1.z.array(exports.DashboardWidgetId),
    /** True while no one has chosen and `widgets` is the built-in default. */
    isDefault: zod_1.z.boolean(),
    /** Widgets this tenant is entitled to, so the picker can show what is on offer. */
    available: zod_1.z.array(exports.DashboardWidgetId),
    configVersion: zod_1.z.number().int().min(1),
    updatedAt: zod_1.z.string().datetime().nullable(),
    updatedBy: zod_1.z.string().nullable(),
});
/**
 * Replacing the selection. Order is meaningful — it is the order the tiles
 * render in — so this is a list, not a set, and duplicates are rejected rather
 * than quietly collapsed.
 */
exports.UpdateDashboardWidgetsRequest = zod_1.z
    .object({
    widgets: zod_1.z
        .array(exports.DashboardWidgetId)
        .max(exports.DASHBOARD_WIDGETS.length)
        .refine((list) => new Set(list).size === list.length, 'widgets must not repeat'),
})
    .strict();
/** `?date=YYYY-MM-DD` — omitted means today in the tenant's resolved zone. */
exports.DashboardSummaryQuery = zod_1.z
    .object({
    date: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
})
    .strict();
//# sourceMappingURL=dashboard.js.map